import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { Flex, Link } from 'nallian-shared-ui/lib/components';
import { useNavigate } from 'react-router-dom';
import { COMMUNITY_DETAIL_TABS } from '../../constants';
import updateCommunityMemberRolesService from '../../services/communityMemberRoles/updateCommunityMemberRolesService';
import communityMemberService from '../../services/communityMembers/communityMemberService';
import communityMemberRolesService from '../../services/communityMemberRoles/communityMemberRolesService';
import CommunityMemberForm from './CommunityMemberForm';

const EditCommunityMember = ({
  open,
  dispatchCommunityMemberRolesGet,
  dispatchUpdateCommunityMemberRolesPost,
  communityMemberRolesFetch,
  dispatchCommunityMemberGet,
  communityMemberFetch,
  updateCommunityMemberRolesFetch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, actionId } = useParams();

  const handleSubmit = useCallback(
    async body => {
      await dispatchUpdateCommunityMemberRolesPost(
        id,
        body.organisation.id,
        {
          organisationRoles: body.communityRoles,
        },
        () => {
          showToaster('success', t('updateCommunityMemberRolesSuccess'));
          navigate(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`);
        },
        () => {
          showToaster('error', t('updateCommunityMemberRolesFailed'));
          navigate(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`);
        },
        t
      );
    },
    [id, dispatchUpdateCommunityMemberRolesPost, t, navigate]
  );

  useEffect(() => {
    if (open && id && actionId && dispatchCommunityMemberGet && dispatchCommunityMemberRolesGet) {
      dispatchCommunityMemberGet(id, actionId, t);
      dispatchCommunityMemberRolesGet(id, actionId, t);
    }
  }, [id, actionId, open, t, dispatchCommunityMemberRolesGet, dispatchCommunityMemberGet]);

  const defaultData = useMemo(() => {
    return (
      !communityMemberFetch.pending &&
      !communityMemberRolesFetch.pending && {
        ...communityMemberFetch.value,
        communityRoles: communityMemberRolesFetch.value?.communityRoles?.map(cr => cr.tag),
      }
    );
  }, [communityMemberRolesFetch, communityMemberFetch]);

  if (!open) return null;

  return (
    <CommunityMemberForm
      header={
        <Flex>
          {t('editCommunityMember')}&nbsp;-&nbsp;
          <Link to={`/organisations/${communityMemberFetch.value?.organisation?.id}/details`}>
            {communityMemberFetch.value?.organisation?.name}
          </Link>
        </Flex>
      }
      onSubmit={handleSubmit}
      submitProcessing={updateCommunityMemberRolesFetch.pending}
      returnRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`}
      cancelRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`}
      dataLoading={communityMemberFetch.pending}
      data={defaultData}
      showRemoveButton
    />
  );
};

EditCommunityMember.propTypes = {
  dispatchUpdateCommunityMemberRolesPost: PropTypes.func.isRequired,
  updateCommunityMemberRolesFetch: PropTypes.object.isRequired,
  dispatchCommunityMemberRolesGet: PropTypes.func.isRequired,
  communityMemberRolesFetch: PropTypes.object.isRequired,
  dispatchCommunityMemberGet: PropTypes.func.isRequired,
  communityMemberFetch: PropTypes.object.isRequired,
  open: PropTypes.bool,
};

EditCommunityMember.defaultProps = { open: false };

export default compose(
  updateCommunityMemberRolesService,
  communityMemberService,
  communityMemberRolesService
)(EditCommunityMember);
